import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Route } from 'lucide-react';
import ConfirmModal from '../../../components/credits/ConfirmModal';
import {
  AI_ROLE_IDS, AI_ROLE_LABELS, LEGACY_ROUTING_ROLE_IDS, putRegistryRow, registryRowId,
  type AiRoleId,
} from '../../../services/aiControlApi';
import type { AiTabProps } from '../AiControlCenter';
import RoutingRoleCard from './RoutingRoleCard';
import { buildRoleDraft, buildRolePatch, roleDiff, type RoleDraft } from './routingDraft';
import { RubyLoader } from '../../../components/RubyLoader';

type DraftMap = Record<string, RoleDraft>;

const RoutingTab: React.FC<AiTabProps> = ({
  registry, registryError, reloadRegistry, onHeaderStats, showToast,
}) => {
  const [drafts, setDrafts] = useState<DraftMap>({});
  const [savingRole, setSavingRole] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<AiRoleId | null>(null);

  const originals: DraftMap = useMemo(() => {
    const map: DraftMap = {};
    for (const id of AI_ROLE_IDS) {
      map[id] = buildRoleDraft(registry?.roles.find((r) => r.roleId === id), id);
    }
    return map;
  }, [registry]);

  // Reset every draft whenever the registry reloads — a stale draft on top of
  // fresh rows would let an operator overwrite someone else's edit blindly.
  useEffect(() => { setDrafts(originals); }, [originals]);

  useEffect(() => {
    const live = LEGACY_ROUTING_ROLE_IDS.filter((id) => originals[id]?.enabled).length;
    onHeaderStats([
      { label: 'Roles', value: `${live}/${LEGACY_ROUTING_ROLE_IDS.length}`, tone: 'emerald' },
      { label: 'Providers', value: String(registry?.providers.length ?? 0), tone: 'white' },
      { label: 'Models', value: String(registry?.models.length ?? 0), tone: 'blue' },
    ]);
  }, [originals, registry, onHeaderStats]);

  const doSave = async (roleId: AiRoleId) => {
    const draft = drafts[roleId];
    if (!draft) return;
    setSavingRole(roleId);
    try {
      await putRegistryRow('role', registryRowId('role', { roleId }), buildRolePatch(draft));
      showToast(`${AI_ROLE_LABELS[roleId]} routing saved`, 'success');
      await reloadRegistry();
    } catch (err: any) {
      showToast(err?.message || `Failed to save ${AI_ROLE_LABELS[roleId]}`, 'error');
    } finally {
      setSavingRole(null);
      setConfirming(null);
    }
  };

  if (registryError) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-sm">
        <p className="font-semibold">Failed to load the AI registry</p>
        <p className="text-xs mt-0.5">{registryError}</p>
      </div>
    );
  }
  if (!registry) {
    return (
      <RubyLoader variant="page" className="bg-white border border-stone-200 rounded-xl" />
    );
  }

  const pendingDiff = confirming ? roleDiff(originals[confirming], drafts[confirming]) : [];

  return (
    <>
      <div className="bg-white border border-stone-200 rounded-xl shadow-sm px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Route size={14} className="text-rose-600" />
          Role routing
        </h2>
        <p className="text-[11px] text-gray-500 mt-0.5">
          The fallback for every caller not yet converted to a Ruby AI Action — never
          names a vendor. Point a role at a cheaper model and every caller follows on
          the next request — no deploy, no code change. Image and video generation now
          route through the Ruby AI Actions routing map below, not a role.
        </p>
      </div>

      {registry.models.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-gray-700">
            No models are registered, so no role can be routed. Run Plan 3's migration
            script to project the legacy registries into <code className="bg-white px-1 rounded text-[11px]">MODEL#</code> rows.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {LEGACY_ROUTING_ROLE_IDS.map((roleId) => (
          <RoutingRoleCard
            key={roleId}
            roleId={roleId}
            original={originals[roleId]}
            draft={drafts[roleId] ?? originals[roleId]}
            providers={registry.providers}
            models={registry.models}
            saving={savingRole === roleId}
            onChange={(next) => setDrafts((d) => ({ ...d, [roleId]: next }))}
            onReset={() => setDrafts((d) => ({ ...d, [roleId]: originals[roleId] }))}
            onSave={() => setConfirming(roleId)}
          />
        ))}
      </div>

      <ConfirmModal
        open={!!confirming}
        title={confirming ? `Save ${AI_ROLE_LABELS[confirming]} routing?` : ''}
        tone="rose"
        confirmLabel="Save role"
        busy={!!savingRole}
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && void doSave(confirming)}
        message={
          <div className="space-y-2">
            <p className="text-sm text-gray-700">
              This takes effect on the next AI call platform-wide, within the router's
              60-second cache. It does not require a deploy.
            </p>
            <ul className="text-xs font-mono bg-stone-50 border border-stone-200 rounded p-2 space-y-1">
              {pendingDiff.map((d) => (
                <li key={String(d.field)}>
                  {String(d.field)}: {String(d.before)} → <strong>{String(d.after)}</strong>
                </li>
              ))}
            </ul>
          </div>
        }
      />
    </>
  );
};

export default RoutingTab;
